import MonetaryUtils from "../../../shared/utils/MonetaryUtils";
import {
	buildCycleMonths,
	formatCycleIdentifier
} from "./dashboard-cycle-utils";

const getMonthEndTimestamp = ({ mes, ano }) => {
	if (!mes || !ano) {
		return null;
	}

	return new Date(Number(ano), Number(mes), 0).getTime();
};

const formatSeriesPoint = (
	point,
	type,
	valueKey = "value"
) => ({
	...point,
	date: getMonthEndTimestamp(point),
	month: String(point?.nomeMes || "").toUpperCase(),
	value: point?.valor ?? 0,
	[valueKey]: point?.valor ?? 0,
	type,
	raw: point
});

const formatSeries = (
	series = [],
	type,
	valueKey = "value"
) => {
	return Array.isArray(series)
		? series.map(point => formatSeriesPoint(point, type, valueKey))
		: [];
};

export const normalizeGoals = goals => {
	return Array.isArray(goals)
		? goals.map(goal => ({
			minimumValue: goal?.valorMinimoReais ?? goal?.valorMinimoRs ?? 0,
			discountPercentage: goal?.percentualDesconto ?? 0,
			raw: goal
		}))
		: [];
};

export const formatDashboardInstallment = installment => {
	const installmentNumber = installment?.numeroParcela;
	const discountPercentage = installment?.percentualDesconto ?? 0;

	return {
		...installment,
		installmentNumber,
		originalValue: installment?.valorOriginal ?? 0,
		discountPercentage,
		discountValue: installment?.valorDesconto ?? 0,
		discountedValue: installment?.valorComDesconto ?? 0,
		label: `Parcela ${installmentNumber}`,
		value: MonetaryUtils.formatPercentage(discountPercentage),
		raw: installment
	};
};

export const formatDashboardCycleResponse = dashboardData => {
	const data = dashboardData?.resultado ?? dashboardData ?? {};

	const discount = data?.desconto || {};
	const justification = discount?.justificativa || {};
	const monthlySales = data?.vendas?.mensal || {};
	const dailySales = data?.vendas?.diario || {};
	const varilux = data?.varilux || {};
	const grossSales = data?.grossSales || {};
	const formattedCycle = formatCycleIdentifier(data?.ciclo);

	const cycleYear =
		formattedCycle.year ||
		data?.ano ||
		new Date().getFullYear();

	const months = buildCycleMonths({
		cycle: formattedCycle.apiValue,
		year: cycleYear
	});

	const cycle = {
		label: formattedCycle.year
			? `${formattedCycle.year} - ${formattedCycle.apiValue}`
			: formattedCycle.apiValue,
		value: formattedCycle.selectValue,
		apiValue: formattedCycle.apiValue,
		cycle: formattedCycle.apiValue,
		year: formattedCycle.year,
		isOpen: Boolean(data.aberto),
		isClosed: !data.aberto,
		startDate: months[0]?.startDate,
		endDate: months[months.length - 1]?.endDate,
		months
	};

	const installments = Array.isArray(data?.parcelas)
		? data.parcelas.map(formatDashboardInstallment)
		: [];

	return {
		cycle,
		isOpen: Boolean(data.aberto),
		isClosed: !data.aberto,
		calculationMode: data.modoCalculo,
		installments,
		purchaseSummary: null,
		cycleStatus: {
			discountPercentage: discount.percentualDesconto ?? 0,
			isBelowBaseline: justification.metaGrupoAlcancada === false,
			hasLatePaymentInPeriod:
				justification.inadimplenciaNoPeriodo ?? null,
			goals: {
				minimumSales: Boolean(justification.metaMinimaVendasAlcancada),
				varilux: Boolean(justification.metaVariluxAlcancada),
				group: Boolean(justification.metaGrupoAlcancada)
			}
		},
		sales: {
			monthly: {
				accumulatedMonth: monthlySales.acumuladoMes ?? 0,
				endMonthProjection: monthlySales.projecaoFimMes ?? 0,
				projectedAccumulatedAverage:
					monthlySales.mediaAcumuladaProjetada ?? 0,
				monthlyBaseline: monthlySales.baselineMensal ?? 0,
				trend: monthlySales.tendencia,
				discountPercentage: monthlySales.percentualDesconto ?? 0,
				goals: normalizeGoals(monthlySales.metas),
				currentDay: monthlySales.diaAtual,
				historicalSeries: formatSeries(
					monthlySales.serieHistorica,
					"actual"
				),
				projectedSeries: formatSeries(
					monthlySales.serieProjetada,
					"projection"
				)
			},
			daily: {
				dailyAccumulated: dailySales.acumuladoDiario ?? 0,
				dailyAverage: dailySales.mediaDiaria ?? dailySales.acumuladoDiario ?? 0,
				projectedAccumulatedAverage:
					dailySales.mediaAcumuladaProjetada ?? 0,
				dailyBaseline: dailySales.baselineDiaria ?? 0,
				elapsedBusinessDays: dailySales.diasUteisCorridos ?? 0,
				remainingBusinessDays: dailySales.diasUteisRestantes ?? 0,
				targetDifference: dailySales.diferencaMeta ?? 0,
				trend: dailySales.tendencia,
				discountPercentage: dailySales.percentualDesconto ?? 0,
				goals: normalizeGoals(dailySales.metas),
				currentDay: dailySales.diaAtual,
				historicalSeries: formatSeries(
					dailySales.serieHistorica,
					"actual",
					"dailyAverage"
				),
				projectedSeries: formatSeries(
					dailySales.serieProjetada,
					"projection",
					"dailyAverage"
				)
			}
		},
		varilux: {
			status: varilux.status,
			isAboveGoal: varilux.status === "ACIMA_DA_META",
			monthPairs: varilux.paresNoMes ?? 0,
			endMonthProjection: varilux.projecaoFimMes ?? 0,
			projectedAccumulatedAverage:
				varilux.mediaAcumuladaProjetada ?? 0,
			trend: varilux.tendencia,
			variluxGoal: varilux.metaVarilux ?? 0,
			variluxBaseline: varilux.baselineVarilux ?? 0,
			closedSeries: formatSeries(
				varilux.serieFechados,
				"actual",
				"pairsAverage"
			),
			projectedSeries: formatSeries(
				varilux.serieProjetada,
				"projection",
				"pairsAverage"
			)
		},
		grossSales: {
			mode: grossSales.modoUtilizado,
			conversionFactor: grossSales.fatorConversao,
			displayIndex: grossSales.indiceExibicao,
			warning: grossSales.aviso
		},
		raw: data
	};
};